import { zodResolver } from '@hookform/resolvers/zod';
import { formatLanguage, formatRegion, Input, OptionCards, Select } from '@platform/design-system';
import { Controller, useForm } from 'react-hook-form';
import { useTenantDetail } from '../catalog/useCatalogQueries.ts';
import { QueryView } from '../QueryView.tsx';
import { usePageContext } from '../usePageContext.ts';
import type { StepViewProps } from './step-flow.ts';
import {
  STORE_NAME_MAX_LENGTH,
  storeFormOf,
  storeFormSchema,
  timeZoneChoicesOf,
  type StoreFormValues,
  type TenantView,
} from './store-profile.ts';
import styles from './Setup.module.css';

const StoreForm = ({ api, flow, tenant }: StepViewProps & { tenant: TenantView }) => {
  const { t, locale, invalidate } = usePageContext();
  const form = useForm<StoreFormValues>({
    resolver: zodResolver(storeFormSchema),
    defaultValues: storeFormOf(tenant),
    mode: 'onBlur',
  });
  return (
    <form
      id={flow.formId}
      className={styles['form']}
      noValidate
      onSubmit={(event) => {
        void form.handleSubmit((values) => {
          flow.submit(async () => {
            await api.put('/admin/tenant/profile', { body: values });
            await invalidate('tenant');
          });
        })(event);
      }}
    >
      <Input
        label={t('setup.storeName')}
        required
        maxLength={STORE_NAME_MAX_LENGTH}
        helperText={t('setup.storeNameHelp')}
        errorText={form.formState.errors.name ? t('setup.storeNameInvalid') : undefined}
        {...form.register('name')}
      />
      <Controller
        control={form.control}
        name="defaultLocale"
        render={({ field }) => (
          <OptionCards
            label={t('setup.language')}
            columns={3}
            value={field.value}
            onChange={field.onChange}
            options={tenant.locales.map((code) => ({
              value: code,
              title: formatLanguage(code, code),
              description: formatLanguage(code, locale),
            }))}
          />
        )}
      />
      <p className={styles['muted']}>{t('setup.languageHelp')}</p>
      <div className={styles['pair']}>
        <Input
          label={t('setup.country')}
          readOnly
          value={t('setup.countryValue', {
            country: formatRegion(tenant.country, locale),
            currency: tenant.currency,
          })}
          helperText={t('setup.countryHelp')}
        />
        <Select
          label={t('setup.timezone')}
          helperText={t('setup.timezoneHelp')}
          options={timeZoneChoicesOf(tenant.timezone).map((zone) => ({ value: zone, label: zone }))}
          {...form.register('timezone')}
        />
      </div>
    </form>
  );
};

export const StoreStep = (props: StepViewProps) => {
  const { t } = usePageContext();
  return (
    <QueryView query={useTenantDetail(props.api)} label={t('setup.storeTitle')} height="320px">
      {(tenant) => <StoreForm {...props} tenant={tenant} />}
    </QueryView>
  );
};
